'use client';

import {
  type ComponentProps,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from 'react';

import { Button as ThriftButton } from '@depop/web-thrift-ui/Button';
import { Tick } from '@depop/web-thrift-ui/Icons/Tick';
import { Button as LegacyButton } from '@depop/web-ui-kit/Button';

import { ErrorMessage } from '@/components/ErrorMessage';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';

type ThriftProps = ComponentProps<typeof ThriftButton>;

type LegacyColor = ComponentProps<typeof LegacyButton>['color'];

type LegacySize = ComponentProps<typeof LegacyButton>['size'];

export type ButtonExperimentProps = ThriftProps & {
  errorText?: string;
  isSuccess?: boolean;
  legacyComponent?: ComponentType;
  legacyClassName?: string;
  legacyIsSuccess?: boolean;
  legacyOutline?: boolean;
  legacyIcon?: ReactElement<Record<string, unknown>>;
  legacyColor?: LegacyColor;
  legacySize?: LegacySize;
  legacyChildren?: ReactNode;
};

const LEGACY_SIZE = {
  small: 'sm',
  regular: 'md',
  large: 'lg',
} as const;

type ImplProps = {
  as?: 'button' | 'a' | 'span';
  variant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'small' | 'regular' | 'large';
  icon?: ReactElement<Record<string, unknown>>;
  iconAlignment?: 'leading' | 'trailing';
  inverted?: boolean;
  isLoading?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  children?: ReactNode;
  errorText?: string;
  isSuccess?: boolean;
  legacyComponent?: ComponentType;
  legacyClassName?: string;
  legacyIsSuccess?: boolean;
  legacyOutline?: boolean;
  legacyIcon?: ReactElement<Record<string, unknown>>;
  legacyColor?: LegacyColor;
  legacySize?: LegacySize;
  legacyChildren?: ReactNode;
} & Record<string, unknown>;

export function ButtonExperiment(props: ButtonExperimentProps) {
  const { web_thrift_ui_button: decision } = useExperiments([
    'web_thrift_ui_button',
  ]);

  const {
    errorText,
    isSuccess,
    legacyComponent,
    legacyClassName,
    legacyIsSuccess,
    legacyOutline,
    legacyIcon,
    legacyColor,
    legacySize,
    legacyChildren,
    className: _className,
    ...thriftProps
  } = props as unknown as ImplProps;

  const Legacy = legacyComponent ?? LegacyButton;
  const error = errorText ? <ErrorMessage>{errorText}</ErrorMessage> : null;
  const legacyRendersError = Boolean(legacyComponent && errorText);

  if (decision === VARIANT_IDENTIFIER) {
    const successProps = isSuccess
      ? {
          icon: <Tick size="sm" />,
          iconAlignment: 'leading',
          isLoading: false,
        }
      : {};

    return (
      <>
        <ThriftButton
          {...({ ...thriftProps, ...successProps } as unknown as ThriftProps)}
        />
        {error}
      </>
    );
  }

  const {
    as,
    variant = 'primary',
    size = 'large',
    icon,
    iconAlignment = 'leading',
    inverted: _inverted,
    isLoading = false,
    fullWidth,
    disabled,
    type,
    children,
    ...passthrough
  } = thriftProps;

  const isNativeButton = as === undefined || as === 'button';
  const legacySuccess = legacyIsSuccess ?? isSuccess;
  const legacyLoading = isLoading && !legacySuccess;

  return (
    <>
      <Legacy
        {...passthrough}
        as={as}
        outline={legacyOutline ?? variant !== 'primary'}
        size={legacySize ?? LEGACY_SIZE[size]}
        color={legacyColor}
        icon={legacyIcon ?? icon}
        iconPosition={iconAlignment === 'trailing' ? 'right' : 'left'}
        isLoading={legacyLoading}
        isSuccess={legacySuccess}
        className={legacyClassName}
        block={fullWidth}
        {...(legacyRendersError ? { errorText } : {})}
        {...(isNativeButton
          ? {
              type: type ?? 'button',
              disabled: disabled || legacyLoading,
              'aria-busy': legacyLoading || undefined,
            }
          : {})}
      >
        {legacyChildren ?? children}
      </Legacy>
      {!legacyRendersError && error}
    </>
  );
}
